import React, { useState } from 'react';
import { Calculator, CheckCircle2, ArrowRight, Bot, FileText, Clock, Shield, Sparkles } from 'lucide-react';
import { VirtualBuilder } from '../components/foundation/VirtualBuilder';

export function CreateFoundation() {
  const [showBuilder, setShowBuilder] = useState(false);

  const features = [
    {
      icon: <Bot className="w-6 h-6" />,
      title: "AI-Driven Textoptimering",
      description: "Vår akademiska AI hjälper dig skriva professionella texter"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Juridiska Dokument",
      description: "Färdiga mallar och dokument för alla stiftelsetyper"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Bank & Revision",
      description: "Direktkoppling till specialiserade partners"
    }
  ];

  const traditionalCosts = [
    {
      title: "Registreringsavgifter",
      amount: "1 500 - 3 000 kr",
      description: "Avgift till Länsstyrelsen"
    },
    {
      title: "Juridisk hjälp",
      amount: "5 000 - 15 000 kr",
      description: "Upprättande av stadgar och registrering"
    },
    {
      title: "Administration",
      amount: "5 000 - 10 000 kr",
      description: "Löpande kostnader första året"
    },
    {
      title: "Övriga kostnader",
      amount: "2 000 - 5 000 kr",
      description: "Diverse uppstartskostnader"
    },
    {
      title: "Tidsåtgång",
      amount: "8-12 månader",
      description: "Omfattande process med många iterationer"
    },
    {
      title: "Konsulttjänster",
      amount: "120 000 - 180 000 kr",
      description: "Professionell hjälp genom hela processen"
    }
  ];

  const pricingOptions = [
    {
      title: "Engångsbetalning",
      price: "19 900 kr",
      savings: "Spara upp till 160 100 kr",
      features: [
        "Komplett digital stiftelsebyggare",
        "AI-driven textoptimering",
        "Alla juridiska dokument",
        "Direktkoppling till myndigheter",
        "2 timmar juridisk rådgivning",
        "Professionell granskning",
        "Registrering via juridisk byrå",
        "Livstids uppdateringar",
        "Klar på 3-6 månader"
      ]
    },
    {
      title: "Delbetalning",
      price: "6 900 kr",
      period: "/ mån i 3 månader",
      total: "Totalt 20 700 kr",
      features: [
        "Samma innehåll som engångsbetalning",
        "Uppdelat på 3 månader",
        "Ingen ränta",
        "Ingen kreditprövning",
        "Första betalning idag",
        "Klar på 3-6 månader"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1]/10 to-white">
      <div className="max-w-7xl mx-auto px-4 py-16">
        {!showBuilder ? (
          <>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                Starta din egen stiftelse
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Spara månader av research och arbete med vår digitala stiftelsebyggare.
                Vi har automatiserat hela processen och gjort det möjligt för vem som
                helst att starta en stiftelse - snabbt, enkelt och professionellt.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-rose-600 mb-3">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#A3C1DA]/5 to-[#F2C1D1]/5 rounded-3xl p-8 mb-16">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Spara tid och pengar
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Jämför traditionella kostnader och tidsåtgång med vår digitala lösning
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12 mb-16">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <Calculator className="w-8 h-8 text-gray-400" />
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Traditionell process
                    </h3>
                  </div>
                  <div className="space-y-6">
                    {traditionalCosts.map((cost, index) => (
                      <div key={index} className="flex justify-between items-start border-b border-gray-100 pb-4">
                        <div>
                          <h4 className="font-medium text-gray-900">{cost.title}</h4>
                          <p className="text-sm text-gray-500">{cost.description}</p>
                        </div>
                        <span className="font-semibold text-gray-900">{cost.amount}</span>
                      </div>
                    ))}
                    <div className="pt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Total kostnad</span>
                        <span className="font-bold text-lg text-gray-900">133 500 - 213 000 kr</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        Exklusive grundkapital (100 000 - 500 000 kr)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-6">
                  {pricingOptions.map((option, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-lg border-2 border-[#A3C1DA]">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {option.title}
                      </h3>
                      <div className="flex items-end gap-2 mb-4">
                        <span className="text-3xl font-bold text-[#A3C1DA]">
                          {option.price}
                        </span>
                        {option.period && (
                          <span className="text-gray-500 mb-1">{option.period}</span>
                        )}
                      </div>
                      {option.total && (
                        <p className="text-sm text-gray-500 mb-4">{option.total}</p>
                      )}
                      <p className="text-green-600 font-medium mb-6">{option.savings}</p>
                      <ul className="space-y-3 mb-6">
                        {option.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => setShowBuilder(true)}
                        className="w-full bg-[#A3C1DA] text-white py-3 rounded-lg hover:bg-[#92B0C9] transition-colors flex items-center justify-center gap-2"
                      >
                        Starta nu
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500">
                  * Alla priser är inklusive moms. Grundkapital för stiftelsen tillkommer enligt dina önskemål.
                </p>
              </div>
            </div>
          </>
        ) : (
          <VirtualBuilder onBack={() => setShowBuilder(false)} />
        )}
      </div>
    </div>
  );
}